import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { firstValueFrom, Subscription, timer } from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {NumberHelper} from '../../helper/NumberHelper';
import {EventCategory, EventType, TrackingService} from '../../service/tracking.service';
import {TimeService} from '../../service/time.service';

@Component({
  selector: 'app-timer',
  templateUrl: './timer.component.html',
  styleUrls: ['./timer.component.scss']
})
export class TimerComponent implements OnInit {

  @Input() warnThreshold = 600;
  @Input() infoThreshold = 1800;
  @Input() clientShiftThreshold = 300;

  @Input() set timerUntil(time: Date | undefined) {
    if (time && !this.waitUntil && !this.subscription) {
      this.addTimer(time || new Date());
    }
  }

  @Input() timerFinished = false;

  @Output() timerEnded = new EventEmitter<void>();
  @Output() clientShiftUpdated = new EventEmitter<number>();
  @Output() clientShiftDetected = new EventEmitter<Date>();

  waitUntil!: Date;
  private subscription?: Subscription | null;
  private endReported = false;
  private clientShiftReported = false;

  clientTimeShift = 0;
  lastServerCheck?: Date;
  lastTick?: Date;

  serverTimeFetchInProgress = false;
  tickInProgress = false;

  constructor(private cdRef: ChangeDetectorRef,
              private timeService: TimeService,
              private trackingService: TrackingService) {
  }

  ngOnInit(): void {
  }

  private async syncServerTime() {
    this.serverTimeFetchInProgress = true;
    const time = await firstValueFrom(this.timeService.getCurrentServerTime());
    this.clientTimeShift = new Date().getTime() - time.getTime();
    this.lastServerCheck = new Date();
    this.lastTick = new Date();
    this.clientShiftUpdated.emit(this.clientTimeShift);
    this.serverTimeFetchInProgress = false;

    console.log(`Synced server time, client time shift ${this.clientTimeShift} ms`);

    const difference = Math.abs(this.clientTimeShift) / 1000;
    if (difference > this.clientShiftThreshold) {
      if (!this.clientShiftReported) {
        this.clientShiftDetected.emit(time);
        this.clientShiftReported = true;
      }
      this.trackingService.sendEvent(EventType.contest_exam_client_time_shift, EventCategory.contest_exam, 'seconds', difference);
    }
  }

  private addTimer(waitUntil: Date) {
    this.waitUntil = waitUntil;
    this.syncServerTime().then(() => {
      this.subscription = timer(0, 1000)
        .pipe(
          tap(() => {
            if (this.timerFinished) {
              this.endReported = true;
              this.clearTimer();
            }
          }),
          filter(() => {
            return !this.serverTimeFetchInProgress && !this.tickInProgress;
          }),
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          tap(async () => {
            this.tickInProgress = true;
            if ((Math.abs(new Date().getTime() - (this.lastTick?.getTime() || 0)) >= 15 * 1000)
              || (Math.abs(new Date().getTime() - (this.lastServerCheck?.getTime() || 0)) >= 5 * 60 * 1000)) {
              // more than 15 seconds after the last tick or 5 min after last server sync, need to sync with server
              await this.syncServerTime();
            } else {
              this.lastTick = new Date();
            }

            if (this.endDateWithShift <= new Date().getTime() && !this.serverTimeFetchInProgress) {
              this.clearTimer();
            }

            this.tickInProgress = false;
          }),
        )
        .subscribe(() => {
          if (!this.serverTimeFetchInProgress) this.cdRef.detectChanges();
        });
    }).catch(err => console.error(err));
  }

  public clearTimer() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    if (!this.endReported) {
      this.endReported = true;
      this.timerEnded.emit();
    }
  }

  get endDateWithShift(): number {
    return this.waitUntil.getTime() + this.clientTimeShift;
  }

  get waitTime() {
    return (this.endDateWithShift - new Date().getTime()) / 1000;
  }

  get waitTimeLabel() {
    if (this.endDateWithShift > new Date().getTime()) {
      const waitTime = this.waitTime;
      const minutes = Math.floor(waitTime / 60);
      const seconds = waitTime - minutes * 60;
      return `${NumberHelper.pad(minutes, 2)}:${NumberHelper.pad(parseInt((seconds > 59 ? 59 : seconds).toFixed()), 2)}`;
    } else {
      return '0';
    }
  }
}
